/**
 * InplayPage.jsx
 * Premium Dark-mode Live Sports Betting Page
 * Custom badge layouts and responsive live odds layouts
 */
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './InplayPage.css';
import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsnewContext } from '../../../_context/SportsnewContext';
import FigmaBetCart from './FigmaBetCart';

// SVG icons for sport tabs
import soccerIcon from '../../../assets/Games/1.svg';
import basketballIcon from '../../../assets/Games/2.svg';
import baseballIcon from '../../../assets/Games/3.svg';
import hockeyIcon from '../../../assets/Games/4.svg';
import volleyballIcon from './assets/valleyball.png';

// Jersey/dress image
import dressImage from '../../../assets/away_team_red.png';
import awayTeamBlueImage from '../../../assets/away_team_blue.png';

// Lucide icons
import { BarChart2, ReceiptText, Loader, Lock } from 'lucide-react';
import { IppMatchListSkeleton, IppMarketDetailSkeleton } from '../../../components/Skeleton/Skeleton';
import { sortMarketRow, deriveGroupKey, oddRowLineLabel, groupMarketsByType } from '../_helpers/marketRowSorting';
import { formatSportsMatchTime } from '../_helpers/sportsMatchTime';
import { buildSportsnewMarketTabs, ensureSportsnewMarketTab, marketMatchesSportsnewCategory } from '../_helpers/sportsnewMarketCategories';
import { getLiveHeaderText, getSportsnewSportLabel } from '../_helpers/liveDisplay';
import { mainOddsResultLabel } from '../_helpers/mainOddsPolicy';
import { countryFlagEmoji, resolveSportsnewCountryName } from '../_helpers/countryFlags';
import { isOddLocked, marketIdentityKey } from '../../../_context/_helpers/sportsnewListPatch';
import { SPORTSNEW_LIST_STATUS } from '../../../_context/_helpers/sportsnewListRequestState';
import { resolveLiveRouteSportFilter } from '../_helpers/sportsPageState';
import getSelectionLabel from './selectionLabelResolver';

/* ── Helpers ── */
const fmtScore = (f) => {
  const h = f.score?.home ?? f.live_score_home ?? f.score_home ?? f.scoreHome ?? '0';
  const a = f.score?.away ?? f.live_score_away ?? f.score_away ?? f.scoreAway ?? '0';
  return `${h} : ${a}`;
};
const fmtPeriod = (f) => {
  const formatted = getLiveHeaderText(f);
  if (formatted) return formatted;
  const p = f.period ?? f.live_period ?? f.periodNumber ?? '';
  const m = f.clock ?? f.live_clock ?? f.live_minute ?? f.minute ?? '';
  if (!p && !m) return '';
  if (p && m) return `${p} ${m}'`;
  return p || `${m}'`;
};
// Fixed Asia/Seoul formatting — never the browser's local timezone.
const fmtMatchTime = (f) => formatSportsMatchTime(f);
const fmtOdds = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n.toFixed(2) : '—'; };
const STALE_EXPLICIT_MATCH_GRACE_MS = 5000;
const LIVE_STATUS_SET = new Set(['live', 'inplay', 'in_play', 'in-progress', 'in_progress', 'playing']);
const isLiveFixtureRow = (fixture) => {
  if (!fixture) return false;
  const sourceMode = String(fixture.sourceMode ?? fixture.source_mode ?? '').trim().toLowerCase();
  if (sourceMode && sourceMode !== 'live') return false;
  if (sourceMode === 'live') return true;
  if (fixture.is_live === true || fixture.isLive === true || fixture.live === true) return true;
  const status = String(fixture.status ?? fixture.fixtureStatus ?? fixture.fixture_status ?? '').trim().toLowerCase();
  return LIVE_STATUS_SET.has(status);
};
// Mirrors InternationalPage.jsx's normaliseSportSlug — same sport set as
// SPORTSNEW_SPORTS (SportsnewContext.jsx), including Korean fallback matches
// since the API can return either.
const getSportId = (f) => {
  const s = (typeof f.sport === 'string' ? f.sport : (f.sport?.slug ?? f.sport?.name ?? '')).toLowerCase();
  if (s.includes('soccer') || s.includes('football') || s.includes('축구')) return 'soccer';
  if (s.includes('basketball') || s.includes('농구')) return 'basketball';
  if (s.includes('baseball') || s.includes('야구')) return 'baseball';
  if (s.includes('icehockey') || s.includes('ice-hockey') || s.includes('hockey') || s.includes('하키')) return 'ice-hockey';
  if (s.includes('volleyball') || s.includes('배구')) return 'volleyball';
  if (s.includes('esport') || s.includes('이스포츠')) return 'esports';
  return 'other';
};

// Custom inline SVG icons
const GridIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ipp_sport_icon_svg">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const EsportsEmojiIcon = ({ className = 'ipp_sport_icon_svg', size = 24 }) => (
  <span
    className={className}
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size,
      lineHeight: 1,
    }}
  >
    🎮
  </span>
);

const SPORT_CHIPS = [
  { id: 'all', icon: <GridIcon />, label: '전체' },
  { id: 'soccer', icon: <img src={soccerIcon} alt="축구" className="ipp_sport_icon_img" />, label: '축구' },
  { id: 'basketball', icon: <img src={basketballIcon} alt="농구" className="ipp_sport_icon_img" />, label: '농구' },
  { id: 'baseball', icon: <img src={baseballIcon} alt="야구" className="ipp_sport_icon_img" />, label: '야구' },
  { id: 'ice-hockey', icon: <img src={hockeyIcon} alt="아이스하키" className="ipp_sport_icon_img" />, label: '아이스하키' },
  { id: 'volleyball', icon: <img src={volleyballIcon} alt="배구" className="ipp_sport_icon_img" />, label: '배구' },
  { id: 'esports', icon: <EsportsEmojiIcon size={24} />, label: 'e스포츠' },
];

const SPORT_CHIP_META = {
  soccer: { icon: <img src={soccerIcon} alt="축구" className="ipp_sport_icon_img" />, label: '축구' },
  basketball: { icon: <img src={basketballIcon} alt="농구" className="ipp_sport_icon_img" />, label: '농구' },
  baseball: { icon: <img src={baseballIcon} alt="야구" className="ipp_sport_icon_img" />, label: '야구' },
  'ice-hockey': { icon: <img src={hockeyIcon} alt="아이스하키" className="ipp_sport_icon_img" />, label: '아이스하키' },
  volleyball: { icon: <img src={volleyballIcon} alt="배구" className="ipp_sport_icon_img" />, label: '배구' },
  esports: { icon: <EsportsEmojiIcon size={24} />, label: 'e스포츠' },
};
const liveSportChipsFromCategories = (categories) => {
  const rows = Array.isArray(categories)
    ? categories.filter((category) => category?.active !== false && category?.liveEnabled !== false)
    : [];
  if (rows.length === 0) return SPORT_CHIPS;
  return [
    SPORT_CHIPS[0],
    ...rows.map((category) => ({
      id: category.slug,
      icon: SPORT_CHIP_META[category.slug]?.icon || SPORT_CHIPS[0].icon,
      label: category.nameKo || category.name || SPORT_CHIP_META[category.slug]?.label || category.slug,
    })),
  ];
};

const InplayPage = () => {
  const location = useLocation();
  const { selections, toggleSelection, isSelected } = useContext(SportsBetCartContext);
  const {
    categories,
    fixtures,
    loading,
    listStatus,
    error: listError,
    fetchFixtures,
    mode,
    displayScope,
    setMode,
    setDisplayScope,
    selectedSport: ctxSelectedSport,
    setSelectedSport: setCtxSport,
    selectedLeagueId: ctxSelectedLeagueId,
    setSelectedLeagueId: setCtxLeagueId,
    selectedGameTitleId: ctxSelectedGameTitleId,
    setSelectedGameTitleId: setCtxGameTitleId,
    selectedTournamentId: ctxSelectedTournamentId,
    setSelectedTournamentId: setCtxTournamentId,
    detailFixture,
    detailMarkets,
    detailLoading,
    loadFixtureDetail,
    clearFixtureDetail,
    oddsFlash,
    page,
    totalPages,
    goToPage,
    leagueCountryMap,
    operatorSuspendedFixtureIds,
  } = useContext(SportsnewContext);

  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  // Derived from context (not local state) — the sport filter must always match
  // whichever sport the fixtures list is actually scoped to, whether it was
  // set from this page's own chips or from the FigmaLeftRail sidebar. Keeping
  // this as independent local state let the two UIs disagree, so a sidebar
  // sport change could leave the list filtered against a sport nothing matches.
  const selectedSport = useMemo(() => {
    if (!ctxSelectedSport) return 'all';
    return typeof ctxSelectedSport === 'object' ? (ctxSelectedSport.slug ?? 'all') : ctxSelectedSport;
  }, [ctxSelectedSport]);
  const sportChips = useMemo(() => liveSportChipsFromCategories(categories), [categories]);
  const routeSportFilter = useMemo(
    () => resolveLiveRouteSportFilter(location.search, categories),
    [location.search, categories],
  );
  const [activeMatch, setActiveMatch] = useState(null);
  const activeMatchSourceRef = useRef(null);
  const explicitMatchSelectedAtRef = useRef(0);
  const [activeCategoryTab, setActiveCategoryTab] = useState('all');

  // Set live mode on mount; clear any stale prematch detail left by InternationalPage
  useEffect(() => {
    clearFixtureDetail();
    setMode('live');
    setDisplayScope('live');
    setCtxLeagueId?.(null);
    setCtxGameTitleId?.(null);
    setCtxTournamentId?.(null);
    return () => { setCtxSport(null); };
  }, [setMode, setDisplayScope, setCtxSport, setCtxLeagueId, setCtxGameTitleId, setCtxTournamentId, clearFixtureDetail]);

  useEffect(() => {
    if (routeSportFilter.resolved) setCtxSport(routeSportFilter.sport);
    else if (categories.length > 0) setCtxSport(null);
  }, [routeSportFilter, categories.length, setCtxSport]);

  useEffect(() => {
    if (selectedSport === 'all') return;
    if (sportChips.some((chip) => chip.id === selectedSport)) return;
    setCtxSport(null);
  }, [selectedSport, sportChips, setCtxSport]);

  useEffect(() => {
    setActiveMatch(null);
    clearFixtureDetail();
  }, [selectedSport, ctxSelectedLeagueId, ctxSelectedGameTitleId, ctxSelectedTournamentId, page, clearFixtureDetail]);

  // Build normalised live matches from real fixture data
  const normalisedFixtures = useMemo(() => {
    const dedupMap = new Map();
    for (const f of fixtures) {
      if (!isLiveFixtureRow(f)) continue;
      const raw = f.fixtureId ?? f.id;
      if (raw == null) continue;
      const key = String(raw);
      if (!dedupMap.has(key)) dedupMap.set(key, f);
    }
    return Array.from(dedupMap.values()).map((f, index) => {
      const sportId = getSportId(f);
      const mo = f.mainOdds ?? {};

      const _homeOdd = mo.home?.oddStateId ? {
      ...mo.home,
      id: mo.home.oddStateId,
      oddStateId: mo.home.oddStateId,
      odds: mo.home.priceDecimal,
      priceDecimal: mo.home.priceDecimal,
      name: mo.home.label ?? 'Home',
      bettable: mo.home.bettable ?? false,
    } : null;

      const _drawOdd = mo.draw?.oddStateId ? {
      ...mo.draw,
      id: mo.draw.oddStateId,
      oddStateId: mo.draw.oddStateId,
      odds: mo.draw.priceDecimal,
      priceDecimal: mo.draw.priceDecimal,
      name: mo.draw.label ?? 'Draw',
      bettable: mo.draw.bettable ?? false,
    } : null;

      const _awayOdd = mo.away?.oddStateId ? {
      ...mo.away,
      id: mo.away.oddStateId,
      oddStateId: mo.away.oddStateId,
      odds: mo.away.priceDecimal,
      priceDecimal: mo.away.priceDecimal,
      name: mo.away.label ?? 'Away',
      bettable: mo.away.bettable ?? false,
    } : null;

    // Whether the moneyline market EXISTS for this fixture at all — NOT whether
    // it's currently bettable. Gating the whole row on "any bettable" made it
    // disappear entirely the moment all three slots got locked/suspended (a
    // routine, frequent occurrence on live matches) and reappear once one
    // unlocked. The slots must stay fixed; each button already shows "—" and a
    // disabled/suspended state individually when its own odd isn't bettable.
      const hasAnyOdds = !!(_homeOdd || _drawOdd || _awayOdd);

    // No nested league/country object exists on a real fixture row
    // (confirmed against real captured list payloads) — country name comes
    // from the navigation-tree cross-reference (leagueCountryMap) instead,
    // and the flag is derived from that name client-side, not a backend image.
      const countryName = resolveSportsnewCountryName(f, leagueCountryMap);

      return {
      _raw: f,
      id: String(f.fixtureId ?? f.id),
      serverOrder: f.rowOrder ?? f.row_order ?? index,
      countryName,
      countryFlag: countryFlagEmoji(countryName),
      leagueName: f.leagueName ?? f.league?.name ?? '',
      leagueSport: getSportsnewSportLabel(f),
      sportId,
      home: f.home ?? f.home_team?.name ?? f.home_team ?? '홈팀',
      away: f.away ?? f.away_team?.name ?? f.away_team ?? '원정팀',
      homeLogo: f.homeLogo || null,
      awayLogo: f.awayLogo || null,
      score: fmtScore(f),
      period: fmtPeriod(f),
      timeLabel: fmtMatchTime(f),
      showOdds: hasAnyOdds,
      marketCount: f.marketCount ?? 0,
      odds: hasAnyOdds ? {
        homeMatch: _homeOdd?.bettable ? fmtOdds(_homeOdd.odds) : '—',
        drawOdds: _drawOdd ? (_drawOdd.bettable ? fmtOdds(_drawOdd.odds) : '—') : null,
        awayMatch: _awayOdd?.bettable ? fmtOdds(_awayOdd.odds) : '—',
      } : null,
      _homeOdd,
      _drawOdd,
      _awayOdd,
    };
  });
  }, [fixtures, leagueCountryMap]);

  const filteredMatches = useMemo(() =>
    normalisedFixtures.filter((m) => selectedSport === 'all' || m.sportId === selectedSport),
  [normalisedFixtures, selectedSport]);
  const liveFilterReady = mode === 'live' && displayScope === 'live';

  // List hydration never opens a detail; only an explicit card click may do so.
  useEffect(() => {
    activeMatchSourceRef.current = null;
    setActiveMatch(null);
    clearFixtureDetail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport, ctxSelectedLeagueId, ctxSelectedGameTitleId, ctxSelectedTournamentId, page, liveFilterReady]);

  // Keep the selected fixture hydrated as WS list patches update clock, score, and period.
  // If an explicitly clicked live fixture briefly leaves the list during lock/hide churn,
  // keep the detail panel pinned for a short grace window. Auto-selected fixtures must
  // never outlive the current live list, otherwise prematch rows can be queried as live detail.
  useEffect(() => {
    if (!liveFilterReady) {
      activeMatchSourceRef.current = null;
      setActiveMatch(null);
      return;
    }
    setActiveMatch((prev) => {
      if (!filteredMatches.length) {
        if (
          prev
          && !operatorSuspendedFixtureIds?.has(String(prev.id))
          && activeMatchSourceRef.current === 'explicit'
          && Date.now() - explicitMatchSelectedAtRef.current < STALE_EXPLICIT_MATCH_GRACE_MS
        ) {
          return prev;
        }
        activeMatchSourceRef.current = null;
        return null;
      }
      if (!prev) return null;
      const hydrated = filteredMatches.find((m) => m.id === prev.id);
      if (hydrated) return hydrated;
      if (
        !operatorSuspendedFixtureIds?.has(String(prev.id))
        && activeMatchSourceRef.current === 'explicit'
        && Date.now() - explicitMatchSelectedAtRef.current < STALE_EXPLICIT_MATCH_GRACE_MS
      ) {
        return prev;
      }
      activeMatchSourceRef.current = null;
      return null;
    });
  }, [filteredMatches, liveFilterReady, operatorSuspendedFixtureIds]);

  useEffect(() => {
    if (!activeMatch && filteredMatches.length === 0) clearFixtureDetail();
  }, [activeMatch, filteredMatches.length, clearFixtureDetail]);

  const handleSportChange = (sportId) => {
    // Push sport filter to context → triggers server-side filtered fixture fetch.
    // `selectedSport` itself is derived from context, so this is the only write needed.
    setCtxLeagueId?.(null);
    setCtxGameTitleId?.(null);
    setCtxTournamentId?.(null);
    setCtxSport(sportId === 'all' ? null : sportId);
    setActiveMatch(null);
  };

  const handleMatchClick = (match) => {
    activeMatchSourceRef.current = 'explicit';
    explicitMatchSelectedAtRef.current = Date.now();
    setActiveMatch(match);
    setActiveCategoryTab('all');
    setDetailOpen(true);
    clearFixtureDetail();
    loadFixtureDetail(match.id);
  };

  const handleOddsClick = (fixture, odd) => {
    if (!odd || !odd.bettable) return;
    const homeTeam = fixture.home ?? fixture.home_team?.name ?? fixture.home_team ?? '';
    const awayTeam = fixture.away ?? fixture.away_team?.name ?? fixture.away_team ?? '';
    toggleSelection({
      odds_id: odd.id ?? odd.oddStateId,
      match_id: fixture.fixtureId ?? fixture.id,
      market_id: odd.market_id ?? null,
      market_group_key: `${fixture.fixtureId ?? fixture.id}:mainOdds`,
      shortOddId: odd.shortOddId ?? null,
      canonicalSelectionKey: odd.canonicalSelectionKey ?? null,
      canonicalLineKey: odd.canonicalLineKey ?? null,
      canonicalPairKey: odd.canonicalPairKey ?? null,
      points: odd.points ?? odd.line ?? null,
      line: odd.line ?? odd.points ?? null,
      selectionLine: odd.selectionLine ?? odd.normalizedSelection ?? null,
      groupingKey: odd.groupingKey ?? odd.canonicalPairKey ?? null,
      odds: odd.priceDecimal ?? odd.odds,
      match_name: `${homeTeam} vs ${awayTeam}`,
      market_name: mainOddsResultLabel(fixture.mainOdds),
      selection_name_display: getSelectionLabel(odd, { match: fixture, fallback: odd.label ?? odd.name }),
    });
  };

  // Real markets for the active match (from detail or inline), grouped into
  // rows by STABLE identity (marketFamily/periodScope/lineKey/pairKey — see
  // marketRowSorting.js), never by array position. The previous
  // odds.slice(i*2, i*2+2) positional chunking here had no concept of line
  // or team identity at all: a market bundling multiple lines (e.g. several
  // totals thresholds) or a team yes/no market (home_yes/home_no/away_yes/
  // away_no) would be paired purely by whatever order the API happened to
  // return them in, silently mispairing or visually flattening distinct
  // lines/selections together.
  const liveMarketSource = useMemo(() => (
    detailMarkets.length > 0
      ? detailMarkets
      : (detailFixture?.markets ?? activeMatch?._allMarkets ?? [])
  ), [detailMarkets, detailFixture, activeMatch]);

  const liveMarketTabs = useMemo(() => buildSportsnewMarketTabs({
    fixture: detailFixture,
    markets: liveMarketSource,
    sport: activeMatch?.sportId,
  }), [detailFixture, liveMarketSource, activeMatch?.sportId]);

  useEffect(() => {
    const next = ensureSportsnewMarketTab(liveMarketTabs, activeCategoryTab);
    if (next !== activeCategoryTab) setActiveCategoryTab(next);
  }, [liveMarketTabs, activeCategoryTab]);

  const liveDetailMarkets = useMemo(() => {
    const markets = liveMarketSource.filter((market) =>
      marketMatchesSportsnewCategory(market, activeCategoryTab));
    const home = activeMatch?.home;
    const away = activeMatch?.away;
    // The API sends one array entry per LINE, sharing one type identity
    // (marketTypeKey / marketStateId) across e.g. Asian Total Goals'
    // 1.5/2.5/3.5 — merge those back into one logical market before
    // splitting into rows, so every line lands as a ROW under one shared
    // section instead of three separate repeated sections.
    return groupMarketsByType(markets).map((market) => {
      const odds = market._mergedOdds ?? market.selections ?? market.odds ?? [];
      const rowMap = new Map();
      const rowOrder = [];
      odds.forEach((odd) => {
        const key = deriveGroupKey(odd, market);
        if (!rowMap.has(key)) { rowMap.set(key, []); rowOrder.push(key); }
        rowMap.get(key).push(odd);
      });
      const rows = rowOrder.map((k) => sortMarketRow(rowMap.get(k), home, away));
      return {
        ...market,
        _rows: rows,
        // Market titles stay line-free; every row/selection already carries
        // its own line via oddRowLineLabel.
        _lineLabel: '',
      };
    });
  }, [liveMarketSource, activeCategoryTab, activeMatch]);

  const initialListLoading = (
    listStatus === SPORTSNEW_LIST_STATUS.IDLE
    || listStatus === SPORTSNEW_LIST_STATUS.LOADING
  ) && filteredMatches.length === 0;
  const listFailed = listStatus === SPORTSNEW_LIST_STATUS.ERROR;
  const listEmpty = listStatus === SPORTSNEW_LIST_STATUS.SUCCESS_EMPTY
    && filteredMatches.length === 0;

  return (
    <div className="ipp_page">
      <div className="ipp_shell">

        {/* ═══ LEFT PANEL: Matches list & Sports chips ═══ */}
        <div className="ipp_left_col">

          {/* Sports Filter Chips */}
          <div className="ipp_sport_chips">
            {sportChips.map(chip => (
              <button
                key={chip.id}
                type="button"
                className={`ipp_sport_chip ${selectedSport === chip.id ? 'ipp_sport_chip_active' : ''}`}
                onClick={() => handleSportChange(chip.id)}
              >
                {chip.icon && (
                  <div className="ipp_sport_icon_wrapper">
                    {chip.icon}
                  </div>
                )}
                <span className="ipp_sport_label">{chip.label}</span>
              </button>
            ))}
          </div>

          {/* Live matches list with specific card structure */}
          <div className="ipp_match_list">
            {initialListLoading && <IppMatchListSkeleton count={4} />}
            {listFailed && (
              <div className="ipp_list_error" role="alert">
                <span>{listError || '경기 목록을 불러오지 못했습니다.'}</span>
                <button type="button" onClick={() => fetchFixtures()} disabled={loading}>다시 시도</button>
              </div>
            )}
            {listEmpty && (
              <div className="ipp_no_data">진행 중인 라이브 경기가 없습니다.</div>
            )}
            {!initialListLoading && filteredMatches.map((match) => {
                const isActive = activeMatch?.id === match.id;
                return (
                  <div
                    key={match.id}
                    className={`ipp_match_card ${isActive ? 'ipp_match_card_active' : ''}`}
                    onClick={() => handleMatchClick(match)}
                  >
                    {/* Card Header */}
                    <div className="ipp_card_header">
                      <div className="ipp_header_left">
                        <span className="ipp_card_flag_emoji" role="img" aria-label={match.countryName || '국제'}>{match.countryFlag}</span>
                        <span className="ipp_card_league">{match.countryName ? `${match.countryName} - ${match.leagueName}` : match.leagueName}</span>
                        <span className="ipp_card_sep">|</span>
                        <span className="ipp_card_sport">{match.leagueSport}</span>
                      </div>
                      <div className={`ipp_header_right_stats ${isActive ? 'ipp_stats_active' : ''}`}>
                        <BarChart2 size={16} />
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="ipp_card_body">
                      {/* Home Team (Jersey + Name) */}
                      <div className="ipp_body_team_home">
                        <img src={match.homeLogo || dressImage} className="ipp_jersey_img" alt="Home Jersey" />
                        <span className="ipp_body_team_name">{match.home}</span>
                      </div>

                      {/* Live Center (Period + Score) */}
                      <div className="ipp_body_center_live">
                        <span className="ipp_live_period_badge">{match.period}</span>
                        <span className="ipp_live_score_display">{match.score}</span>
                      </div>

                      {/* Away Team (Name + Blue Tinted Jersey) */}
                      <div className="ipp_body_team_away">
                        <span className="ipp_body_team_name">{match.away}</span>
                        <img src={match.awayLogo || awayTeamBlueImage} className="ipp_jersey_img ipp_jersey_away" alt="Away Jersey" />
                      </div>

                      {/* Date/Time box */}
                      <div className="ipp_body_date_box">
                        <span>{match.timeLabel.split(' ')[0]}</span>
                        <span>{match.timeLabel.split(' ')[1]}</span>
                      </div>
                    </div>

                    {/* Card Odds Section */}
                    {match.showOdds && match.odds && (
                      <div className="ipp_card_odds_section">
                        {/* Odds Row 1: Match Winner (승무패) */}
                        <div className="ipp_card_odds_row">
                          <button
                            type="button"
                            className={`ipp_card_odds_cell ipp_cell_left ${isSelected(match._homeOdd?.id) ? 'ipp_cell_selected' : ''} ${!match._homeOdd?.bettable ? 'dp_cell_suspended' : ''} ${oddsFlash[match._homeOdd?.id] ? `dp_odds_flash_${oddsFlash[match._homeOdd?.id]}` : ''}`}
                            disabled={!match._homeOdd?.bettable}
                            onClick={(e) => { e.stopPropagation(); handleOddsClick(match._raw, match._homeOdd); }}
                          >
                            <div className="ipp_odds_cell_team_wrap">
                              <img src={match.homeLogo || dressImage} className="ipp_odds_jersey_img" alt="Home Jersey" />
                              <span className="ipp_card_cell_team">{match.home}</span>
                            </div>
                            <span className="ipp_card_cell_odds">{match.odds.homeMatch}</span>
                          </button>

                          {match._drawOdd && (
                            <button
                              type="button"
                              className={`ipp_card_odds_cell ipp_cell_mid ${isSelected(match._drawOdd?.id) ? 'ipp_cell_selected' : ''} ${!match._drawOdd?.bettable ? 'dp_cell_suspended' : ''} ${oddsFlash[match._drawOdd?.id] ? `dp_odds_flash_${oddsFlash[match._drawOdd?.id]}` : ''}`}
                              disabled={!match._drawOdd?.bettable}
                              onClick={(e) => { e.stopPropagation(); handleOddsClick(match._raw, match._drawOdd); }}
                            >
                              <span className="ipp_card_cell_badge">Draw</span>
                              <span className="ipp_card_cell_odds">{match.odds.drawOdds}</span>
                            </button>
                          )}

                          <button
                            type="button"
                            className={`ipp_card_odds_cell ipp_cell_right ${isSelected(match._awayOdd?.id) ? 'ipp_cell_selected' : ''} ${!match._awayOdd?.bettable ? 'dp_cell_suspended' : ''} ${oddsFlash[match._awayOdd?.id] ? `dp_odds_flash_${oddsFlash[match._awayOdd?.id]}` : ''}`}
                            disabled={!match._awayOdd?.bettable}
                            onClick={(e) => { e.stopPropagation(); handleOddsClick(match._raw, match._awayOdd); }}
                          >
                            <span className="ipp_card_cell_odds">{match.odds.awayMatch}</span>
                            <div className="ipp_odds_cell_team_wrap">
                              <span className="ipp_card_cell_team">{match.away}</span>
                              <img src={match.awayLogo || awayTeamBlueImage} className="ipp_odds_jersey_img ipp_jersey_away" alt="Away Jersey" />
                            </div>
                          </button>

                          {/* Label reflects the ACTUAL mainOdds shape (draw
                              slot present or not), never a sport lookup —
                              this naturally matches sport policy (soccer/
                              ice-hockey: 3-way 승무패; basketball/volleyball/
                              baseball/eSports: 2-way 승패) as long as the API
                              sends the right shape, without hardcoding it. */}
                          <div className="ipp_card_odds_label">{mainOddsResultLabel(match._raw?.mainOdds)}</div>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
          </div>

          {totalPages > 1 && (
            <div className="ipp_pagination" aria-label="라이브 경기 페이지">
              <button
                type="button"
                className="ipp_page_btn"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                ‹
              </button>
              <span className="ipp_page_info">{page} / {totalPages}</span>
              <button
                type="button"
                className="ipp_page_btn"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >
                ›
              </button>
            </div>
          )}

        </div>

        {/* ═══ RIGHT PANEL: Odds markets ═══ */}
        <div className={`ipp_right_col ${detailOpen ? 'ipp_right_col_mobile_open' : ''}`}>
          {/* Back button for mobile details drawer */}
          <button
            type="button"
            className="ipp_detail_close_btn"
            onClick={() => setDetailOpen(false)}
          >
            ← 뒤로가기
          </button>

          <div className="ipp_detail_panel">
            {activeMatch && (
              <div className="ipp_live_match_summary">
                <div className="ipp_live_match_team ipp_live_match_team_home">
                  <img src={activeMatch.homeLogo || dressImage} className="ipp_live_match_team_logo" alt="" />
                  <span className="ipp_live_match_team_name">{activeMatch.home}</span>
                </div>
                <div className="ipp_live_match_center">
                  <span className="ipp_live_match_status">{activeMatch.period || '경기중'}</span>
                  <span className="ipp_live_match_score">{activeMatch.score}</span>
                  <span className="ipp_live_match_time">{activeMatch.timeLabel}</span>
                </div>
                <div className="ipp_live_match_team ipp_live_match_team_away">
                  <span className="ipp_live_match_team_name">{activeMatch.away}</span>
                  <img src={activeMatch.awayLogo || awayTeamBlueImage} className="ipp_live_match_team_logo" alt="" />
                </div>
              </div>
            )}

            {!activeMatch && !initialListLoading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#94a3b8', fontSize: '13px', fontWeight: 500 }}>
                {listFailed
                  ? '경기 목록을 다시 불러와 주세요.'
                  : (listEmpty ? '진행 중인 라이브 경기가 없습니다.' : '경기를 선택해 주세요.')}
              </div>
            )}

            {/* ═══ Category Tabs + Markets — only when a match is active ═══ */}
            {activeMatch && (
            <>
            <div className="ipp_category_tabs">
              {liveMarketTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`ipp_category_tab ${activeCategoryTab === tab.id ? 'ipp_category_tab_active' : ''}`}
                  onClick={() => setActiveCategoryTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ═══ Market Sections — real data from API ═══ */}
            <div className="ipp_market_sections">
              {detailLoading && <IppMarketDetailSkeleton count={4} />}

              {/* Real markets from API */}
              {!detailLoading && liveDetailMarkets.length > 0 && liveDetailMarkets.map((market) => {
                const marketName = market.name ?? market.market_name ?? '마켓';
                const marketId = market.marketStateId ?? market.id ?? market.market_id;
                // A single marketStateId can be reused across multiple DISTINCT
                // markets (different lines/periods/team-scoped variants) — the
                // React key (and the betslip's market_group_key below) must use
                // the fuller identity (marketGroupId first, else a composite
                // fallback), not marketId alone, or two such markets collide
                // and only one of them ends up rendered/selectable.
                const groupKey = marketIdentityKey(market) ?? marketId ?? marketName;
                return (
                  <div key={groupKey} className="ipp_market_section">
                    <div className="ipp_market_section_title">{marketName}{market._lineLabel}</div>
                    <div className="ipp_market_section_content">
                      {/* One row per stable identity group (line/pair) — see
                          marketRowSorting.js. Multiple lines in the same
                          market now render as separate rows instead of being
                          paired purely by array position. */}
                      {(market._rows ?? []).map((row) => {
                        // A merged multi-line section holds several distinct
                        // lines under one groupKey now — the betslip's
                        // exclusivity key must stay scoped to THIS row's own
                        // line, or e.g. "Over 2.5" and "Over 1.5" would
                        // wrongly collide as if they were the same market.
                        const rowGroupKey = `${groupKey}:${deriveGroupKey(row[0], market)}`;
                        return (
                        <div key={rowGroupKey} className="ipp_market_row">
                        {row.map((odd) => {
                          const oddId = odd.oddStateId ?? odd.id;
                          const priceValue = odd.priceDecimal ?? odd.odds ?? odd.price;
                          const isLocked = isOddLocked(odd);
                          const isBettable = !isLocked && priceValue != null;
                          const oddLabel = getSelectionLabel(odd, { market, match: activeMatch });
                          const oddKey = oddId ?? `${rowGroupKey}:${odd.name ?? oddLabel}`;
                          return (
                            <button
                              key={oddKey}
                              type="button"
                              className={`ipp_market_cell_btn ${isSelected(oddId) ? 'ipp_market_cell_btn_selected' : ''} ${isLocked ? 'dp_cell_locked' : ''} ${!isBettable ? 'dp_cell_suspended' : ''} ${oddsFlash[oddId] ? `dp_odds_flash_${oddsFlash[oddId]}` : ''}`}
                              disabled={!isBettable}
                              onClick={() => {
                                if (!activeMatch || !isBettable) return;
                                toggleSelection({
                                  odds_id: oddId,
                                  match_id: activeMatch.id,
                                  market_id: marketId,
                                  market_group_key: `${activeMatch.id}:${rowGroupKey}`,
                                  shortOddId: odd.shortOddId ?? null,
                                  canonicalSelectionKey: odd.canonicalSelectionKey ?? null,
                                  canonicalLineKey: odd.canonicalLineKey ?? null,
                                  canonicalPairKey: odd.canonicalPairKey ?? null,
                                  points: odd.points ?? odd.line ?? null,
                                  line: odd.line ?? odd.points ?? null,
                                  selectionLine: odd.selectionLine ?? odd.normalizedSelection ?? null,
                                  groupingKey: odd.groupingKey ?? odd.canonicalPairKey ?? null,
                                  odds: priceValue,
                                  match_name: `${activeMatch.home} vs ${activeMatch.away}`,
                                  market_name: marketName,
                                  selection_name_display: oddLabel,
                                });
                              }}
                            >
                              <span className="ipp_market_cell_label">{oddLabel}{oddRowLineLabel(odd, market)}</span>
                              {isLocked
                                ? <span className="dp_cell_locked_odds"><Lock size={10} strokeWidth={2.5} />{fmtOdds(priceValue)}</span>
                                : <span className="ipp_market_cell_value">{fmtOdds(priceValue)}</span>
                              }
                            </button>
                          );
                        })}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* No markets available */}
              {!detailLoading && liveDetailMarkets.length === 0 && (
                <div className="ipp_market_section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0', color: '#64748b', fontSize: '13px' }}>
                  마켓 정보를 불러올 수 없습니다.
                </div>
              )}

            </div>
            </>
            )}
          </div>
        </div>
      </div>

      {/* Floating betslip button for mobile view */}
      <button
        type="button"
        className="ipp_mobile_cart_fab"
        onClick={() => setMobileCartOpen(prev => !prev)}
      >
        <span className="ipp_mobile_cart_icon_wrap">
          <ReceiptText size={18} />
          {selections.length > 0 && (
            <span className="ipp_mobile_cart_badge">{selections.length}</span>
          )}
        </span>
        <span>베팅카트</span>
      </button>

      {/* Scrim overlay for details drawer */}
      {detailOpen && (
        <div
          className="ipp_detail_scrim"
          onClick={() => setDetailOpen(false)}
        />
      )}

      {/* Mobile BetCart Drawer */}
      <FigmaBetCart
        isMobile
        mobileOpen={mobileCartOpen}
        onMobileClose={() => setMobileCartOpen(false)}
      />
    </div>
  );
};

export default InplayPage;
